import React from "react";
import { HighlightText } from "../../components/HighlightText";
import { interpolate, spring, useVideoConfig } from "remotion";
import { BulletItem } from "../../types";
import { CompanyBadge } from "../../components/CompanyBadge";
import { detectBrandInText } from "../../components/companyDetector";

interface MinimalImpactInsightsProps {
  bullets: BulletItem[];
  globalFrame: number;
}

export const MinimalImpactInsights: React.FC<MinimalImpactInsightsProps> = ({
  bullets,
  globalFrame,
}) => {
  const { fps } = useVideoConfig();
  const currentMs = (globalFrame / fps) * 1000;

  // Identify active bullet
  let activeIndex = bullets.findIndex(
    (b) => currentMs >= b.startMs && currentMs <= b.endMs,
  );

  if (activeIndex === -1) {
    const pastBullets = bullets.filter((b) => b.endMs <= currentMs);
    activeIndex = pastBullets.length > 0 ? pastBullets.length - 1 : 0;
  }

  const activeBullet = bullets[activeIndex];
  const bulletStartFrame = (activeBullet.startMs / 1000) * fps;
  const elapsedFrames = Math.max(0, globalFrame - bulletStartFrame);

  // Snappy spring scaling and sliding entrance for the giant text
  const textOpacity = interpolate(elapsedFrames, [0, 5], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const textTranslateY = interpolate(elapsedFrames, [0, 8], [25, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // Card entrance transition (first bullet start)
  const firstBulletStart = (bullets[0].startMs / 1000) * fps;
  const cardElapsed = Math.max(0, globalFrame - firstBulletStart);

  const cardOpacity = interpolate(cardElapsed, [0, 8], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // Bullet duration progress indicator
  const bulletDurationMs = activeBullet.endMs - activeBullet.startMs;
  const elapsedMs = Math.max(0, currentMs - activeBullet.startMs);
  const progressPct = Math.min(100, (elapsedMs / bulletDurationMs) * 100);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        zIndex: 10,
        margin: "auto 0",
        padding: "0 40px",
        boxSizing: "border-box",
        opacity: cardOpacity,
      }}
    >
      <div
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: "28px",
          position: "relative",
        }}
      >
        {/* Clean layout index tracker + brand badge */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontWeight: 800,
              fontSize: "15px",
              color: "rgba(255,255,255,0.4)",
              letterSpacing: "4px",
            }}
          >
            [ <span style={{ color: "#CCFF00" }}>0{activeIndex + 1}</span> / 0{bullets.length} ]
          </div>
          {(() => {
            const brand = detectBrandInText(activeBullet.text);
            return brand ? (
              <CompanyBadge
                name={brand}
                color="#CCFF00"
                accentColor="#CCFF00"
                size={68}
                style="glow"
                opacity={textOpacity}
              />
            ) : null;
          })()}
        </div>

        {/* Massive insight text block */}
        <div
          style={{
            opacity: textOpacity,
            transform: `translateY(${textTranslateY}px)`,
            minHeight: "380px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <h1
            style={{
              fontFamily: "'Anton', sans-serif",
              fontSize: "66px",
              fontWeight: 900,
              lineHeight: 1.15,
              color: "#FFFFFF",
              textAlign: "left",
              textTransform: "uppercase",
              letterSpacing: "1px",
              margin: 0,
              textShadow: "0 4px 15px rgba(0,0,0,0.6)",
            }}
          >
            <HighlightText
              highlightStyle={{
                fontWeight: 900,
                color: "#CCFF00",
                display: "inline",
              }}
              text={activeBullet.text}
              highlights={activeBullet.highlights}
            />
          </h1>
        </div>

        {/* Minimalist duration progress bar line */}
        <div
          style={{
            width: "100%",
            height: "2px",
            background: "rgba(255, 255, 255, 0.08)",
            borderRadius: "0px",
            overflow: "hidden",
            marginTop: "10px",
          }}
        >
          <div
            style={{
              width: `${progressPct}%`,
              height: "100%",
              background: "#CCFF00",
              transition: "width 0.05s linear",
            }}
          />
        </div>
      </div>
    </div>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
